import React, { useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { Dashboard, TabGroup } from "api/model/dashboard/Dashboard";
import { DashboardTabModel } from "api/model/dashboard/DashboardTab";
import { T } from "services/lang/LanguageService";
import DraggableItem, { ReorderType } from "./tab/reorder/DraggableItem";

interface ReorderTabsAndGroupsProps {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  dashboard: Dashboard;
  setDashboard: (groups: Dashboard) => void;
}

interface ReorderItem {
  uuid: string;
  title: string;
  isTabPlaceholder?: boolean;
}

const ReorderTabsAndGroups = ({
  isVisible,
  setIsVisible,
  dashboard,
  setDashboard,
}: ReorderTabsAndGroupsProps) => {
  const tabGroups = dashboard?.tabGroups ?? [];
  const tabs = dashboard?.dashboardTabsJs ?? [];

  const groupMap = new Map(tabGroups.map(group => [group.uuid, group]));

  const tabCountByGroupId = new Map<string, number>();
  tabs.forEach(tab => {
    if (tab.tabGroupId) {
      tabCountByGroupId.set(tab.tabGroupId, (tabCountByGroupId.get(tab.tabGroupId) ?? 0) + 1);
    }
  });

  const orderedItems: ReorderItem[] = [];

  tabGroups.forEach(group => {
    const groupTabs = tabs.filter(tab => tab.tabGroupId === group.uuid);
    const isSingleTabPlaceholder = group.isTabPlaceholder && groupTabs.length === 1;

    if (isSingleTabPlaceholder) {
      const tab = groupTabs[0];
      orderedItems.push({
        uuid: tab.uuid,
        title: tab.title,
        isTabPlaceholder: true,
      });
    } else if (groupTabs.length > 0) {
      orderedItems.push({
        uuid: group.uuid,
        title: group.title,
        isTabPlaceholder: false,
      });
    }
  });

  const ungroupedTabs = tabs.filter(tab => {
    const group = tab.tabGroupId ? groupMap.get(tab.tabGroupId) : undefined;
    return !group;
  });

  orderedItems.unshift(
    ...ungroupedTabs.map(tab => ({
      uuid: tab.uuid,
      title: tab.title,
      isTabPlaceholder: true,
    }))
  );

  const [items, setItems] = useState<ReorderItem[]>(orderedItems);

  const onDragEnd = (result: any) => {
    if (!result.destination) return;
    const newItems = Array.from(items);
    const [moved] = newItems.splice(result.source.index, 1);
    newItems.splice(result.destination.index, 0, moved);
    setItems(newItems);
  };

  const handleSave = () => {
    const reorderedGroups: TabGroup[] = [];
    const updatedTabs: DashboardTabModel[] = [...dashboard.dashboardTabsJs];

    items.forEach(item => {
      reorderedGroups.push({
        uuid: item.uuid,
        title: item.title,
        isTabPlaceholder: item.isTabPlaceholder,
      });

      if (item.isTabPlaceholder) {
        const matchingTab = updatedTabs.find(tab => tab.uuid === item.uuid);
        if (matchingTab) {
          matchingTab.tabGroupId = item.uuid;
        }
      }
    });

    setDashboard({
      ...dashboard,
      tabGroups: reorderedGroups,
      dashboardTabsJs: updatedTabs,
    });
    setIsVisible(false);
  };

  const footerTemplate = () => (
    <div className="flex justify-content-end gap-2 m-3">
      <Button
        label={T("action.cancel")}
        icon="pi pi-times"
        className="component-editor-button icon-red"
        onClick={() => setIsVisible(false)}
      />
      <Button
        label={T("action.save")}
        icon="pi pi-check"
        className="component-editor-button icon-green"
        onClick={handleSave}
        autoFocus
      />
    </div>
  );

  return (
    <Dialog
      footer={footerTemplate}
      header={T("label.reorder-dropdown-menu-items")}
      visible={isVisible}
      onHide={() => setIsVisible(false)}
      className="w-30rem"
    >
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="reorder-list" direction="vertical">
          {provided => (
            <div ref={provided.innerRef} {...provided.droppableProps} className="tabsFlex column">
              {items.map((item, index) => (
                <Draggable key={item.uuid} draggableId={item.uuid} index={index}>
                  {prov => {
                    const newItem = { ...item };
                    if (!item.isTabPlaceholder) {
                      newItem.title = `Group: ${item.title}`;
                    }

                    return (
                      <DraggableItem
                        item={newItem}
                        provided={prov}
                        type={ReorderType.DD_MENU_ITEMS}
                        classname={`${item.isTabPlaceholder ? "" : "font-bold"}`}
                      />
                    );
                  }}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </Dialog>
  );
};

export default ReorderTabsAndGroups;
